import { useCallback, useState } from 'react'
import {Upload, FileText, BookOpen, Code2, FileCode, FileType, Zap, X} from 'lucide-react'

type DetectedTool = {
  icon: React.ReactNode
  label: string
  description: string
  anchor: string
  accent: string
}

const FORMAT_MAP: Record<string, DetectedTool> = {
  '.pdf': { icon: <FileText size={18} />, label: 'PDF Detected', description: 'Open in PDF Editor, convert to PNG, or apply scan filter.', anchor: '#pdf-editor', accent: 'text-indigo-600 bg-indigo-50' },
  '.epub': { icon: <BookOpen size={18} />, label: 'EPUB Detected', description: 'Route to EPUB → PDF converter with clean margin formatting.', anchor: '#epub-to-pdf', accent: 'text-emerald-600 bg-emerald-50' },
  '.md': { icon: <FileCode size={18} />, label: 'Markdown Detected', description: 'Open split-screen MD editor with live PDF preview.', anchor: '#md-to-pdf', accent: 'text-violet-600 bg-violet-50' },
  '.ipynb': { icon: <Code2 size={18} />, label: 'Jupyter Notebook Detected', description: 'Render notebook cells, outputs, and plots as static PDF.', anchor: '#ipynb-to-pdf', accent: 'text-amber-600 bg-amber-50' },
  '.xml': { icon: <FileType size={18} />, label: 'XML Detected', description: 'Transform schema into print-ready PDF invoice/report.', anchor: '#xml-to-pdf', accent: 'text-rose-600 bg-rose-50' },
  '.pages': { icon: <FileText size={18} />, label: 'Apple Pages Detected', description: 'Convert .pages document to PDF with layout preservation.', anchor: '#pages-to-pdf', accent: 'text-sky-600 bg-sky-50' },
}

const SUPPORTED_EXTENSIONS = Object.keys(FORMAT_MAP)

const STATS = [
  { value: '50+', label: 'PDF Tools' },
  { value: '100%', label: 'Browser-Side' },
  { value: '0', label: 'File Uploads' },
  { value: '100', label: 'Core Web Vitals' },
]

export default function HeroUploader() {
  const [isDragging, setIsDragging] = useState(false)
  const [detectedTool, setDetectedTool] = useState<DetectedTool | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const detectFormat = useCallback((file: File): DetectedTool | null => {
    const name = file.name.toLowerCase()
    for (const ext of SUPPORTED_EXTENSIONS) {
      if (name.endsWith(ext)) return FORMAT_MAP[ext]
    }
    return null
  }, [])

  const handleFile = useCallback((file: File) => {
    const tool = detectFormat(file)
    if (tool) {
      setFileName(file.name)
      setDetectedTool(tool)
      setError(null)
    } else {
      setDetectedTool(null)
      setFileName(file.name)
      setError(`Unsupported format. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`)
    }
  }, [detectFormat])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  const reset = () => {
    setDetectedTool(null)
    setFileName(null)
    setError(null)
  }

  return (
    <section
      id="uploader"
      className="pt-32 pb-16 px-4"
      aria-labelledby="hero-heading"
    >
      <div className="max-w-4xl mx-auto">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
            <Zap size={11} />
            Browser-First · No Uploads · 100% Private
          </span>
        </div>

        {/* Headline */}
        <h1
          id="hero-heading"
          className="text-5xl sm:text-6xl lg:text-7xl font-bold text-center text-gray-900 tracking-tight leading-none mb-5"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          PDF tools that{' '}
          <span className="text-indigo-600 relative inline-block">
            respect your privacy
          </span>
        </h1>

        <p className="text-center text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-12 font-light leading-relaxed">
          Convert, edit, and transform PDF files directly in your browser.{' '}
          <strong className="font-medium text-gray-700">Zero server uploads.</strong>{' '}
          Powered by WebAssembly and the Canvas API.
        </p>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          className={`relative rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer
            ${isDragging
              ? 'border-indigo-400 bg-indigo-50/60 scale-[1.01]'
              : detectedTool
              ? 'border-emerald-300 bg-emerald-50/30'
              : error
              ? 'border-red-300 bg-red-50/30'
              : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/20'
            }`}
          style={{ boxShadow: isDragging ? '0 0 0 4px rgba(79,70,229,0.1)' : 'var(--shadow-card)' }}
          role="button"
          tabIndex={0}
          aria-label="Drop a file here or click to select"
          onKeyDown={(e) => e.key === 'Enter' && document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept={SUPPORTED_EXTENSIONS.join(',')}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            onChange={onInputChange}
            aria-label="Select file to convert"
          />

          <div className="py-14 px-8 text-center">
            {!detectedTool && !error ? (
              <>
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all
                  ${isDragging ? 'bg-indigo-600 text-white scale-110' : 'bg-gray-100 text-gray-400'}`}
                >
                  <Upload size={28} />
                </div>
                <p className="text-base font-semibold text-gray-800 mb-1.5">
                  {isDragging ? 'Release to detect format' : 'Drop your file here'}
                </p>
                <p className="text-sm text-gray-400">
                  Auto-detects: .pdf, .epub, .md, .ipynb, .xml, .pages, .docx, .cdr
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {['.pdf', '.epub', '.md', '.ipynb', '.xml', '.pages'].map((ext) => (
                    <span key={ext} className="font-mono-data text-xs px-2 py-0.5 rounded-md bg-gray-50 text-gray-400 border border-gray-100">
                      {ext}
                    </span>
                  ))}
                </div>
              </>
            ) : detectedTool ? (
              <div className="flex flex-col items-center gap-3">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${detectedTool.accent}`}>
                  {detectedTool.icon}
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-800">{detectedTool.label}</p>
                  <p className="text-sm text-gray-500 mt-0.5 max-w-sm">{detectedTool.description}</p>
                  {fileName && (
                    <p className="font-mono-data text-xs text-gray-400 mt-2">{fileName}</p>
                  )}
                </div>
                <div className="flex gap-3 mt-2">
                  <a
                    href={detectedTool.anchor}
                    className="px-5 py-2 rounded-full text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-all"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open Tool →
                  </a>
                  <button
                    onClick={(e) => { e.stopPropagation(); reset() }}
                    className="px-5 py-2 rounded-full text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all inline-flex items-center gap-1"
                    type="button"
                  >
                    <X size={13} /> Clear
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-red-50 text-red-400">
                  <Upload size={24} />
                </div>
                <p className="text-sm font-medium text-red-600">{error}</p>
                <p className="font-mono-data text-xs text-gray-400">{fileName}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); reset() }}
                  className="px-5 py-2 rounded-full text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all inline-flex items-center gap-1"
                  type="button"
                >
                  <X size={13} /> Try another file
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div
          className="mt-10 grid grid-cols-4 gap-4"
          role="list"
          aria-label="PdfTide statistics"
        >
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="text-center py-4 rounded-xl bg-white border border-gray-100"
              style={{ boxShadow: 'var(--shadow-card)' }}
              role="listitem"
            >
              <p
                className="font-mono-data text-xl font-bold text-indigo-700"
                aria-label={`${stat.value} ${stat.label}`}
              >
                {stat.value}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
